<style>
@import "@/assets/css/custom.css";

.board-info-section {
	border: 1px solid #e9ecef;
	border-radius: 0.375rem;
	overflow: hidden;
	background: #fff;
	margin-bottom: 1rem;
}

.board-info-section .section-head {
	padding: 0.55rem 1rem;
	font-weight: 600;
	font-size: 0.95rem;
	color: #fff;
}

.board-info-section .section-body {
	padding: 0.75rem 1rem;
}

.board-info-row {
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
	padding: 0.4rem 0;
	border-bottom: 1px solid #f1f3f5;
}

.board-info-row:last-child {
	border-bottom: 0;
	padding-bottom: 0;
}

.board-info-row:first-child {
	padding-top: 0;
}

.board-info-label {
	flex: 0 0 42%;
	max-width: 42%;
	color: #6c757d;
	font-size: 0.875rem;
}

.board-info-value {
	flex: 1;
	font-size: 0.9rem;
	word-break: break-word;
}

.board-info-audit .board-info-label {
	flex: none;
	max-width: none;
	display: block;
	margin-bottom: 0.15rem;
}

.board-info-audit .board-info-value {
	font-weight: 600;
}
</style>

<template>
	<div>
		<Breadcrumbs main="Boards" title="Board Info" />

		<div class="container-fluid">
			<div class="card">
				<div class="card-body">
					<div class="d-flex justify-content-between align-items-center mb-3">
						<h5 class="mb-0">Board Details</h5>
						<router-link :to="{ name: 'boards', query: $route.query }" class="btn btn-primary">
							<i class="bx bx-chevrons-left"></i> Back
						</router-link>
					</div>

					<div v-if="loading" class="text-center py-5">
						<span class="spinner-border spinner-border-sm me-2"></span>
						Loading board details…
					</div>

					<div v-else-if="!board">
						<p class="text-muted mb-0">Board not found.</p>
					</div>

					<template v-else>
						<div class="row">
							<div class="col-lg-6">
								<div class="board-info-section">
									<div class="section-head bg-primary">Board data</div>
									<div class="section-body">
										<div class="board-info-row">
											<div class="board-info-label">Board number</div>
											<div class="board-info-value">{{ board.board_number || "—" }}</div>
										</div>
										<div class="board-info-row">
											<div class="board-info-label">Serial number</div>
											<div class="board-info-value">{{ board.serial_number || "—" }}</div>
										</div>
										<div class="board-info-row">
											<div class="board-info-label">Batch number</div>
											<div class="board-info-value">{{ board.batch_number || "—" }}</div>
										</div>
										<div class="board-info-row">
											<div class="board-info-label">Vendor</div>
											<div class="board-info-value">{{ board.vendor_name || "—" }}</div>
										</div>
										<div class="board-info-row">
											<div class="board-info-label">Device model</div>
											<div class="board-info-value">{{ board.device_model_name || board.device_model || "—" }}</div>
										</div>
										<div class="board-info-row">
											<div class="board-info-label">IMEI</div>
											<div class="board-info-value">{{ board.imei || "—" }}</div>
										</div>
										<div class="board-info-row">
											<div class="board-info-label">ICCID</div>
											<div class="board-info-value">{{ board.iccid || "—" }}</div>
										</div>
										<div class="board-info-row">
											<div class="board-info-label">Primary mobile</div>
											<div class="board-info-value">{{ board.primary_mobile_number || "—" }}</div>
										</div>
										<div class="board-info-row">
											<div class="board-info-label">Secondary mobile</div>
											<div class="board-info-value">{{ board.secondary_mobile_number || "—" }}</div>
										</div>
										<div class="board-info-row">
											<div class="board-info-label">Network provider</div>
											<div class="board-info-value">{{ board.network_provider || board.sim || "—" }}</div>
										</div>
										<div class="board-info-row">
											<div class="board-info-label">Firmware version</div>
											<div class="board-info-value">{{ board.firmware_version || "—" }}</div>
										</div>
										<div class="board-info-row">
											<div class="board-info-label">Firmware status</div>
											<div class="board-info-value">
												<span class="badge rounded-pill" :class="firmwareStatusClass(board.firmware_status)">
													{{ firmwareStatusLabel(board.firmware_status) }}
												</span>
											</div>
										</div>
										<div class="board-info-row">
											<div class="board-info-label">Board status</div>
											<div class="board-info-value">
												<span class="badge rounded-pill" :class="boardStatusClass(board.status)">
													{{ boardStatusLabel(board.status) }}
												</span>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div class="col-lg-6">
								<div class="board-info-section">
									<div class="section-head bg-success">Firmware success</div>
									<div class="section-body">
										<div class="board-info-row">
											<div class="board-info-label">Done by</div>
											<div class="board-info-value">{{ board.firmware_success_user_name || "—" }}</div>
										</div>
										<div class="board-info-row">
											<div class="board-info-label">Date / time</div>
											<div class="board-info-value">{{ formatDateTime(board.firmware_success_date) }}</div>
										</div>
									</div>
								</div>

								<div class="board-info-section">
									<div class="section-head bg-danger">Firmware failed</div>
									<div class="section-body">
										<div class="board-info-row">
											<div class="board-info-label">Done by</div>
											<div class="board-info-value">{{ board.firmware_failure_user_name || "—" }}</div>
										</div>
										<div class="board-info-row">
											<div class="board-info-label">Date / time</div>
											<div class="board-info-value">{{ formatDateTime(board.firmware_failure_date) }}</div>
										</div>
									</div>
								</div>

								<div class="board-info-section">
									<div class="section-head bg-info">Enclosure</div>
									<div class="section-body">
										<div class="board-info-row">
											<div class="board-info-label">Done by</div>
											<div class="board-info-value">{{ board.enclosure_user_name || "—" }}</div>
										</div>
										<div class="board-info-row">
											<div class="board-info-label">Date / time</div>
											<div class="board-info-value">{{ formatDateTime(board.enclosure_date) }}</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div class="board-info-section board-info-audit mb-0">
							<div class="section-head bg-secondary">Audit</div>
							<div class="section-body">
								<div class="row">
									<div class="col-md-3 mb-2 mb-md-0">
										<div class="board-info-label">Added by</div>
										<div class="board-info-value">{{ board.added_user_name || "—" }}</div>
									</div>
									<div class="col-md-3 mb-2 mb-md-0">
										<div class="board-info-label">Created at</div>
										<div class="board-info-value">{{ formatDateTime(board.created_at) }}</div>
									</div>
									<div class="col-md-3 mb-2 mb-md-0">
										<div class="board-info-label">Updated by</div>
										<div class="board-info-value">{{ board.updated_user_name || "—" }}</div>
									</div>
									<div class="col-md-3">
										<div class="board-info-label">Updated at</div>
										<div class="board-info-value">{{ formatDateTime(board.updated_at) }}</div>
									</div>
								</div>
							</div>
						</div>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "BoardInfo",
	data() {
		return {
			token: "",
			loading: true,
			board: null,
		};
	},
	mounted() {
		this.getToken();
		this.fetchBoardInfo();
	},
	methods: {
		getToken() {
			const stored = localStorage.getItem("airotrack_staff");
			if (!stored) {
				this.$router.push({ name: "login" });
				return;
			}
			const airotrack_staff = JSON.parse(stored);
			if (!airotrack_staff?.token) {
				this.$router.push({ name: "login" });
				return;
			}
			this.token = airotrack_staff.token;
		},

		async fetchBoardInfo() {
			const boardId = this.$route.params.id;
			if (!boardId) {
				this.loading = false;
				this.$swal("Error", "Board id not provided", "error");
				return;
			}
			this.loading = true;
			try {
				const res = await this.$axios.get("/staffs/boardFullDetails", {
					params: { board_id: boardId },
					headers: { Authorization: "Bearer " + this.token },
				});
				const ok = res.data?.status === true || res.data?.status === "true";
				if (!ok) {
					this.board = null;
					this.$swal("Error", res.data?.message || "Failed to fetch board details", "error");
					return;
				}
				this.board = res.data?.data?.board || null;
			} catch (e) {
				this.board = null;
				this.$swal("Error", e?.response?.data?.message || "Failed to fetch board details", "error");
			} finally {
				this.loading = false;
			}
		},

		formatDateTime(value) {
			if (!value) return "—";
			return this.$filters.formatDateTime(value);
		},

		firmwareStatusLabel(v) {
			const map = { 1: "Pending", 2: "Firmware Success", 3: "Firmware Failed" };
			return map[Number(v)] || "—";
		},

		firmwareStatusClass(v) {
			const map = { 1: "bg-info", 2: "bg-success", 3: "bg-danger" };
			return map[Number(v)] || "bg-secondary";
		},

		boardStatusLabel(v) {
			const map = { 1: "Pending", 2: "Completed", 3: "To Vendor", 4: "Enclosure Pending" };
			return map[Number(v)] || "—";
		},

		boardStatusClass(v) {
			const map = { 1: "bg-warning text-dark", 2: "bg-success", 3: "bg-secondary", 4: "bg-info" };
			return map[Number(v)] || "bg-secondary";
		},
	},
};
</script>
